import { useState, useEffect } from "react";
import { Phone, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { pushSupported, pushPermission, enablePush, refreshPushSubscription } from "@/lib/push";

const isIOS = () => /iP(hone|ad|od)/.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;

// Gentle, dismissible banner that asks members to switch on incoming-call alerts,
// so they can be reached even when Pula isn't open — like a real phone ringing.
export function CallNotifyPrompt() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !pushSupported()) return;
    // Already granted → keep the subscription fresh so rings keep landing, and don't nag.
    if (pushPermission() === "granted") { refreshPushSubscription(); return; }
    // Arrived from a "turn on call alerts" reminder link → show it right away, unmissably.
    if (new URLSearchParams(window.location.search).get("alerts") === "1") { setShow(true); return; }
    // A stronger first-login welcome step handles this — don't also show the banner.
    if (localStorage.getItem("pula_welcome_alerts_pending") === "1") return;
    if (localStorage.getItem("pula_call_notify_dismissed") === "1") return;
    const t = setTimeout(() => setShow(true), 4000);
    return () => clearTimeout(t);
  }, [user]);

  if (!show) return null;

  const iosNeedsInstall = isIOS() && !isStandalone();

  const dismiss = () => {
    localStorage.setItem("pula_call_notify_dismissed", "1");
    setShow(false);
  };

  const turnOn = async () => {
    if (iosNeedsInstall) {
      toast.info("On iPhone: tap the Share icon in Safari, then 'Add to Home Screen', open Pula from there, and turn this on.", { duration: 8000 });
      return;
    }
    setBusy(true);
    try {
      await enablePush();
      toast.success("Call alerts on — you'll now be rung even when Pula is closed. 🌱");
      localStorage.setItem("pula_call_notify_dismissed", "1");
      setShow(false);
    } catch {
      toast.error("Couldn't turn on alerts. Please allow notifications for Pula in your browser.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-4 inset-x-4 md:left-auto md:right-4 md:w-96 z-[90] pula-card p-4 shadow-lg border border-pula-green/30 bg-white"
      data-testid="call-notify-prompt">
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-9 h-9 rounded-full bg-pula-green/15 grid place-items-center text-pula-green">
          <Phone className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <p className="font-heading font-bold text-sm text-pula-ink">Never miss a Pula call</p>
          <p className="text-xs text-pula-inksoft font-body mt-0.5">
            {iosNeedsInstall
              ? "Add Pula to your Home Screen to get a ring when someone calls — even when the app is closed."
              : "Turn on call alerts so you can be reached even when Pula isn't open — like a phone ringing."}
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={turnOn} disabled={busy} data-testid="call-notify-enable"
              className="text-xs font-semibold px-3 py-1.5 rounded-full bg-pula-green text-white hover:bg-pula-greenhover transition-colors disabled:opacity-50">
              {busy ? "Turning on…" : iosNeedsInstall ? "How to enable" : "Turn on call alerts"}
            </button>
            <button onClick={dismiss} data-testid="call-notify-dismiss"
              className="text-xs font-medium text-pula-inksoft hover:text-pula-ink transition-colors">Not now</button>
          </div>
        </div>
        <button onClick={dismiss} aria-label="Dismiss" className="shrink-0 text-pula-inksoft/60 hover:text-pula-ink">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
